import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Typography,
  Chip,
  IconButton,
  Paper,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  CircularProgress,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  useTheme,
  alpha,
  LinearProgress,
  Stack,
  Badge,
  Container,
} from '@mui/material';
import {
  Add,
  AttachFile,
  Delete,
  CloudUpload,
  Save,
  Send,
  OpenInNew,
  Person,
  Email,
  Phone,
  History,
  Warning,
  CheckCircle,
  Error,
  Info,
  ExpandMore,
  ExpandLess,
  Description,
  Image,
  PictureAsPdf,
  InsertDriveFile,
  Close,
  Schedule,
  BugReport,
  Payment,
  Lightbulb,
  HelpOutline,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { PageHeader } from '../components/ui/PageHeader';
import { GlassCard } from '../components/ui/GlassCard';
import { useWebSocket } from '../hooks/useWebSocket';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import UserSelector from '../components/UserSelector';
import { AgentUser } from '../types';
import { socketService } from '../services/socket';
import { useSnackbar } from 'notistack';

interface TicketAttachment {
  id: string;
  name: string;
  size: number;
  type: string;
  preview?: string;
  uploading?: boolean;
  progress?: number;
}


const priorityColors = {
  low: { color: '#10B981', bg: 'rgba(16, 185, 129, 0.1)' },
  normal: { color: '#3B82F6', bg: 'rgba(59, 130, 246, 0.1)' },
  high: { color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.1)' },
  critical: { color: '#EF4444', bg: 'rgba(239, 68, 68, 0.1)' },
};

const categoryIcons = {
  'Technical Issue': <BugReport />,
  'Billing': <Payment />,
  'Feature Request': <Lightbulb />,
  'General Inquiry': <HelpOutline />,
};

const Tickets: React.FC = () => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { socket, isConnected } = useWebSocket();
  const user = useSelector((state: RootState) => state.auth.user);
  const { enqueueSnackbar } = useSnackbar();
  
  const [currentTab, setCurrentTab] = useState(0);
  const [selectedCustomer, setSelectedCustomer] = useState<AgentUser | null>(null);
  const [attachments, setAttachments] = useState<TicketAttachment[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [showInternalNotes, setShowInternalNotes] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  
  const [formData, setFormData] = useState({
    subject: '',
    category: '',
    priority: 'normal',
    description: '',
    affectedSystems: [] as string[],
    environment: '',
    stepsToReproduce: '',
    expectedBehavior: '',
    actualBehavior: '',
    internalNotes: '',
  });


  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    processFiles(files);
  };

  const processFiles = (files: File[]) => {
    const newAttachments: TicketAttachment[] = files.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      name: file.name,
      size: file.size,
      type: file.type,
      uploading: true,
      progress: 0,
    }));

    setAttachments(prev => [...prev, ...newAttachments]);

    // Simulate upload progress with proper cleanup
    newAttachments.forEach((attachment, index) => {
      let cleared = false;
      const interval = setInterval(() => {
        if (cleared) return;
        
        setAttachments(prev => {
          const updated = prev.map(a => {
            if (a.id === attachment.id) {
              const newProgress = Math.min(100, (a.progress || 0) + 10);
              if (newProgress === 100) {
                cleared = true;
                clearInterval(interval);
                return { ...a, uploading: false, progress: 100 };
              }
              return { ...a, progress: newProgress };
            }
            return a;
          });
          return updated;
        });
      }, 200);
    });
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  };

  const removeAttachment = (id: string) => {
    setAttachments(prev => prev.filter(a => a.id !== id));
  };

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return <Image />;
    if (type === 'application/pdf') return <PictureAsPdf />;
    if (type.startsWith('text/')) return <Description />;
    return <InsertDriveFile />;
  };

  const handleSubmit = async (action: 'draft' | 'submit' | 'submitAndOpen') => {
    if (!selectedCustomer || !formData.subject || !formData.category) {
      enqueueSnackbar('Please fill in all required fields', { variant: 'error' });
      return;
    }

    setSubmitting(true);
    
    try {
      const socket = socketService.getSocket();
      if (!socket) {
        enqueueSnackbar('Socket not connected', { variant: 'error' });
        setSubmitting(false);
        return;
      }

      // Combine all ticket details
      const fullDescription = [
        formData.description,
        formData.stepsToReproduce && `\n\nSteps to Reproduce:\n${formData.stepsToReproduce}`,
        formData.expectedBehavior && `\n\nExpected Behavior:\n${formData.expectedBehavior}`,
        formData.actualBehavior && `\n\nActual Behavior:\n${formData.actualBehavior}`,
        formData.internalNotes && `\n\nInternal Notes:\n${formData.internalNotes}`,
        formData.environment && `\n\nEnvironment: ${formData.environment}`,
        formData.affectedSystems.length > 0 && `\n\nAffected Systems: ${formData.affectedSystems.join(', ')}`,
      ].filter(Boolean).join('');

      socket.emit('createTicket', {
        subject: formData.subject,
        description: fullDescription,
        priority: formData.priority,
        category: formData.category,
        type: action === 'draft' ? 'draft' : 'support',
        customerId: selectedCustomer.agentUserId || selectedCustomer.userId,
      }, async (response: any) => {
        if (response.success) {
          enqueueSnackbar('Ticket created successfully', { variant: 'success' });
          
          // Upload attachments if any
          if (attachments.length > 0 && response.ticketId) {
            for (const [index, attachment] of Array.from(attachments.entries())) {
              if (attachment.uploading || !attachment.progress || attachment.progress < 100) continue;
              
              // Convert file to base64 (in a real app, you'd have this from the file read)
              // For now, we'll skip actual upload
              socket.emit('uploadTicketAttachment', {
                ticketId: response.ticketId,
                fileName: attachment.name,
                contentType: attachment.type,
                fileData: '', // Would be base64 encoded file
                isFinal: index === attachments.length - 1,
              }, (uploadResponse: any) => {
                if (uploadResponse.success) {
                  console.log('Attachment uploaded:', attachment.name);
                }
              });
            }
          }

          // Reset form
          setFormData({
            subject: '',
            category: '',
            priority: 'normal',
            description: '',
            affectedSystems: [],
            environment: '',
            stepsToReproduce: '',
            expectedBehavior: '',
            actualBehavior: '',
            internalNotes: '',
          });
          setSelectedCustomer(null);
          setAttachments([]);

          if (action === 'submitAndOpen' && response.ticketId) {
            // Open ticket in new tab (would need portal URL)
            console.log('Would open ticket:', response.ticketId);
          }
        } else {
          enqueueSnackbar(response.error || 'Failed to create ticket', { variant: 'error' });
        }
        setSubmitting(false);
      });
    } catch (error) {
      console.error('Failed to create ticket:', error);
      enqueueSnackbar('Failed to create ticket', { variant: 'error' });
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <PageHeader
        title="Create Support Ticket"
        subtitle="Submit a new support request to our technical team"
        breadcrumbs={[
          { label: 'Home', href: '/dashboard' },
          { label: 'Tickets', href: '/tickets' },
          { label: 'New Ticket' },
        ]}
      />

      <Stack spacing={3}>
        {/* Customer Information Section */}
        <GlassCard gradient>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Person sx={{ mr: 2, color: theme.palette.primary.main }} />
            <Typography variant="h6" fontWeight={600}>
              Customer Information
            </Typography>
            {selectedCustomer && (
              <Chip
                label={selectedCustomer.isOnline ? 'Online' : 'Offline'}
                size="small"
                sx={{ ml: 2 }}
                color={selectedCustomer.isOnline ? 'success' : 'default'}
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            <Box sx={{ flex: 1 }}>
              <UserSelector
                value={selectedCustomer}
                onChange={setSelectedCustomer}
                label="Search Customer"
                placeholder="Type to search by name, email, or company..."
                showCompanyFilter
                required
              />
            </Box>

            {selectedCustomer && (
              <Box sx={{ display: 'flex', gap: 2, flex: { xs: 1, md: '0 0 auto' } }}>
                <Button
                  variant="outlined"
                  startIcon={<History />}
                  fullWidth
                  sx={{ minWidth: 150 }}
                >
                  Recent Tickets
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Email />}
                  fullWidth
                  sx={{ minWidth: 100 }}
                >
                  Contact
                </Button>
              </Box>
            )}
          </Box>
          
          {selectedCustomer && (
            <Box sx={{ 
              mt: 3,
              p: 2, 
              borderRadius: 2,
              background: alpha(theme.palette.primary.main, 0.05),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
            }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">Name</Typography>
                  <Typography variant="body1" fontWeight={500}>{selectedCustomer.fullName}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">Email</Typography>
                  <Typography variant="body1" fontWeight={500}>{selectedCustomer.email}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">Phone</Typography>
                  <Typography variant="body1" fontWeight={500}>{selectedCustomer.Phone || 'Not provided'}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">Account Status</Typography>
                  <Chip 
                    label={selectedCustomer.isOnline ? 'Active' : 'Inactive'} 
                    size="small" 
                    color={selectedCustomer.isOnline ? 'success' : 'default'}
                  />
                </Box>
              </Box>
            </Box>
          )}
        </GlassCard>

        {/* Main Content Area */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3 }}>
          {/* Left Column - Ticket Details */}
          <Box sx={{ flex: '1 1 auto' }}>
            <Stack spacing={3}>
              <GlassCard>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
                  Ticket Details
                </Typography>

                <Stack spacing={3}>
                  <TextField
                    label="Subject"
                    fullWidth
                    required
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="Brief description of the issue"
                  />

                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                    <FormControl fullWidth required>
                      <InputLabel>Category</InputLabel>
                      <Select
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        label="Category"
                        startAdornment={formData.category && categoryIcons[formData.category as keyof typeof categoryIcons]}
                      >
                        {Object.entries(categoryIcons).map(([key, icon]) => (
                          <MenuItem key={key} value={key}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {icon}
                              {key}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl fullWidth required>
                      <InputLabel>Priority</InputLabel>
                      <Select
                        value={formData.priority}
                        onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                        label="Priority"
                      >
                        {Object.entries(priorityColors).map(([key, colors]) => (
                          <MenuItem key={key} value={key}>
                            <Chip
                              label={key.charAt(0).toUpperCase() + key.slice(1)}
                              size="small"
                              sx={{
                                backgroundColor: colors.bg,
                                color: colors.color,
                                fontWeight: 600,
                              }}
                            />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  <TextField
                    label="Description"
                    fullWidth
                    required
                    multiline
                    rows={6}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Detailed description of the issue..."
                  />
                </Stack>
              </GlassCard>

              {/* Additional Information */}
              <GlassCard>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
                  Additional Information
                </Typography>

                <Stack spacing={3}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={formData.stepsToReproduce}
                    onChange={(e) => setFormData({ ...formData, stepsToReproduce: e.target.value })}
                    placeholder="1. Navigate to...\n2. Click on...\n3. Enter...\n4. Observe..."
                    label="Steps to Reproduce"
                  />

                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      value={formData.expectedBehavior}
                      onChange={(e) => setFormData({ ...formData, expectedBehavior: e.target.value })}
                      label="Expected Behavior"
                      placeholder="The system should..."
                    />
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      value={formData.actualBehavior}
                      onChange={(e) => setFormData({ ...formData, actualBehavior: e.target.value })}
                      label="Actual Behavior"
                      placeholder="Instead, the system..."
                    />
                  </Box>
                </Stack>
              </GlassCard>

              {/* Internal Notes */}
              <GlassCard>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                  Internal Notes
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Not visible to customer
                </Alert>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={formData.internalNotes}
                  onChange={(e) => setFormData({ ...formData, internalNotes: e.target.value })}
                  placeholder="Technical details, related issues, workarounds..."
                />
              </GlassCard>
            </Stack>
          </Box>

          {/* Right Column - Environment & Attachments */}
          <Box sx={{ width: { xs: '100%', lg: '400px' } }}>
            <Stack spacing={3}>
              <GlassCard>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
                  Environment & Systems
                </Typography>

                <Stack spacing={3}>
                  <FormControl fullWidth>
                    <InputLabel>Environment</InputLabel>
                    <Select
                      value={formData.environment}
                      onChange={(e) => setFormData({ ...formData, environment: e.target.value })}
                      label="Environment"
                    >
                      <MenuItem value="production">Production</MenuItem>
                      <MenuItem value="staging">Staging</MenuItem>
                      <MenuItem value="development">Development</MenuItem>
                    </Select>
                  </FormControl>

                  <Autocomplete
                    multiple
                    options={['Windows', 'macOS', 'Linux', 'iOS', 'Android', 'Web', 'API', 'Database']}
                    value={formData.affectedSystems}
                    onChange={(e, value) => setFormData({ ...formData, affectedSystems: value })}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Affected Systems"
                        placeholder="Select systems..."
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={key}
                            variant="outlined"
                            label={option}
                            size="small"
                            {...tagProps}
                          />
                        );
                      })
                    }
                  />
                </Stack>
              </GlassCard>

              <GlassCard>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" fontWeight={600}>
                    Attachments
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Max 10MB per file
                  </Typography>
                </Box>

                <Box
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  sx={{
                    border: `2px dashed ${dragActive ? theme.palette.primary.main : alpha(theme.palette.divider, 0.5)}`,
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    backgroundColor: dragActive ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                    transition: 'all 0.2s',
                    cursor: 'pointer',
                  }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    hidden
                    onChange={handleFileSelect}
                  />
                  <CloudUpload sx={{ fontSize: 48, color: theme.palette.text.secondary, mb: 1 }} />
                  <Typography variant="body1" gutterBottom>
                    Drop files here or click to browse
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Supports: Images, PDFs, Documents
                  </Typography>
                </Box>

                {attachments.length > 0 && (
                  <List sx={{ mt: 2 }}>
                    <AnimatePresence>
                      {attachments.map((attachment) => (
                        <motion.div
                          key={attachment.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ duration: 0.2 }}
                        >
                          <ListItem
                            sx={{
                              borderRadius: 1,
                              mb: 1,
                              backgroundColor: alpha(theme.palette.background.paper, 0.5),
                            }}
                            secondaryAction={
                              <IconButton
                                edge="end"
                                onClick={() => removeAttachment(attachment.id)}
                                disabled={attachment.uploading}
                              >
                                <Close />
                              </IconButton>
                            }
                          >
                            <ListItemIcon>
                              {getFileIcon(attachment.type)}
                            </ListItemIcon>
                            <ListItemText
                              primary={attachment.name}
                              secondary={`${(attachment.size / 1024).toFixed(2)} KB`}
                            />
                          </ListItem>
                          {attachment.uploading && (
                            <LinearProgress
                              variant="determinate"
                              value={attachment.progress}
                              sx={{ mt: -1, mb: 1, mx: 2 }}
                            />
                          )}
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </List>
                )}
              </GlassCard>
            </Stack>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Save />}
            onClick={() => handleSubmit('draft')}
            disabled={submitting}
          >
            Save as Draft
          </Button>
          <Button
            variant="contained"
            startIcon={<Send />}
            onClick={() => handleSubmit('submit')}
            disabled={submitting || !selectedCustomer || !formData.subject || !formData.category}
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            }}
          >
            {submitting ? <CircularProgress size={24} /> : 'Submit Ticket'}
          </Button>
          <Button
            variant="contained"
            startIcon={<OpenInNew />}
            onClick={() => handleSubmit('submitAndOpen')}
            disabled={submitting || !selectedCustomer || !formData.subject || !formData.category}
          >
            Submit & Open in Portal
          </Button>
        </Box>
      </Stack>
    </Container>
  );
};

export default Tickets;